import {useEffect, useState} from "react";
import {Breadcrumb, Button, Col, Form} from "react-bootstrap";
import {Controller, useForm} from "react-hook-form";
import {Link, useNavigate, useParams} from "react-router-dom";
import {useNotification} from "../../hooks/useNotification";
import {InputValidationError} from "../../libs/error";

import {yupResolver} from "@hookform/resolvers/yup";
import CircleLoader from "../../components/Loaders/CircleLoader";
import Layout from "../../components/layouts/layout";
import {createLanguage} from "../../services/language";
import {useLanguages} from "../../hooks/useLanguages";
import {LanguageSelector} from "../../components/Language/LanguageSelector";
import {ImageInput} from "../../components/MediaInputs/imageInput";
import {TermLanguageSchema} from "./termSchema";
import {useLang, useTerm} from "../../hooks";
import {getUrlFromStoragePath} from "../../configs";
import {processTermInput} from "../../services/term";
import {BackButton} from "../../components/BackButton/BackButton";

export function TermLanguageCreatePage() {
  const {t} = useLang();
  const {resourceId} = useParams();
  const [isLoading, setIsLoading] = useState();
  const navigate = useNavigate();
  const {notifyError, notifySuccess} = useNotification();

  const {term, isLoading: isTermLoading, error} = useTerm(resourceId)

  const {langs, isLoading: isLangLoading, error: isLangError} = useLanguages();

  if (error || isLangError) {
    notifyError({
      title: "Something went wrong!",
      message: error.message
    })
    navigate(-1)
  }

  const {
    register,
    handleSubmit,
    control,
    formState: {errors},
    setError,
    setValue,
  } = useForm({
    resolver: yupResolver(TermLanguageSchema()),
  });

  useEffect(() => {
    if (term) {
      setValue("name", term.name ?? "");
      setValue("description", term.description ?? "");
    }
  }, [term]);


  const create = async (data) => {
    try {
      setIsLoading(true);
      data.term_id = resourceId
      if (!data.description) {
        delete data.description;
      }
      const l = await createLanguage("term", processTermInput(data))
      notifySuccess({
        title: "Term language created successfully!",
        message: "Language Created",
      });
      navigate(-1);
    } catch (err) {
      if (err instanceof InputValidationError) {
        err.setRHFError(setError);
        notifyError({
          title: "Failed to create term language",
          message: err.message,
        });
        return;
      }
      notifyError({
        title: "Something went wrong",
        message: "Language creation failed!",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      {(isLoading || isTermLoading || isLangLoading) && <CircleLoader/>}
      <header>
        <div className="d-flex justify-content-between align-items-center">
          <Breadcrumb className="d-flex align-items-center">
            <li className="breadcrumb-item">
              {t("terms_manage_terms")}
            </li>
            <li className="breadcrumb-item">
              <Link to={`/langs/term/${resourceId}`}>{term?.name ?? resourceId} Languages</Link>
            </li>
            <Breadcrumb.Item active>Create Language</Breadcrumb.Item>
          </Breadcrumb>
          <BackButton path={-1} text={t("users_page_title")}/>
        </div>
      </header>
      <section id="#term-lang-create">
        <h3>Create Language ({term?.name ?? resourceId})</h3>
        <Form
          onSubmit={handleSubmit(create)}
          className="mt-4 d-flex  flex-column gap-3"
        >

          <LanguageSelector loading={isLangLoading} control={control} langs={langs} errors={errors}/>
          <div className="form-row">
            <Form.Group className="flex-grow-1" controlId="name">
              <Form.Label className="mark-required">Name</Form.Label>
              <Form.Control
                {...register("name")}
                placeholder="Title of Term"
                type="text"
              />
              {errors.name?.message && (
                <Form.Text className="text-danger">
                  {errors.name.message}
                </Form.Text>
              )}
            </Form.Group>
          </div>

          <Form.Group controlId="description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              {...register("description")}
              placeholder="Term description"
              as="textarea"
              rows={3}
            />
            {errors.description?.message && (
              <Form.Text className="text-danger">
                {errors.description.message}
              </Form.Text>
            )}
          </Form.Group>
          <div className="d-flex justify-content-end">
            <Button className="px-4" type="submit">
              Create
            </Button>
          </div>
        </Form>
      </section>
    </Layout>
  );
}
